import React, { useState, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { Form, Collapse, Button, message, Select, Space } from "antd";
import Breadcrumb from "../../components/Breadcrumb";
// Solution Access Element
import SolutionAccessElement from "../../components/Users/SSOIntegration/SolutionAccessElement";
// Users api react query calls
import {
  useGetAllUsers,
  useGetSingleUser,
  useGetApplicationPermission,
  useAddUserApplicationPermissions,
} from "../../hooks/user.hooks";

// Roles api react query calls
import { useGetAllApplicationRolesData } from "../../hooks/applicationRoles.hook";

// Application api react query calls
import { useGetAllApplications } from "../../hooks/applications.hooks";
import { APP_LIST } from "./utils/utils";

const { Panel } = Collapse;
const { Option } = Select;

const itemStyles = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 6 },
    md: { span: 6 },
    lg: { span: 5 },
    xl: { span: 5 },
    xxl: { span: 5 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 18 },
    md: { span: 18 },
    lg: { span: 15 },
    xl: { span: 12 },
    xxl: { span: 12 },
  },
};

const APP_NAMES = APP_LIST.map((item) => item.app_name);

const SSOIntegration = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const { fields, replace } = useFieldArray({
    control,
    name: "config",
  });

  const [userID, setUserID] = useState(undefined);

  // Get User Data
  const { isLoading: isLoadingUserData, data: UsersData } = useGetAllUsers({
    onError: () => message.error("Server Error"),
  });
  // Get Single User Data
  const { data: SingleUserData } = useGetSingleUser(userID, {
    onError: () => message.error("Server Error"),
  });
  // Get Application List
  const { data: ApplicationData } = useGetAllApplications({
    select: (data) => {
      return data.sort((a, b) => {
        return a.app_name.localeCompare(b.app_name);
      });
    },

    onError: () => message.error("Server Error"),
  });

  // Get All Application Roles Data
  const { data: ApplicationRolesList } = useGetAllApplicationRolesData({
    onError: () => message.error("Server Error"),
  });

  // Get Permission Data - already assign application roles
  const { isLoading: isLoadingPermissionData, data: PermissionData } =
    useGetApplicationPermission(userID, {
      onError: () => message.error("Server Error"),
    });

  // Generate User List to Show in Select
  const userList = UsersData?.sort((a, b) => {
    return a.name.localeCompare(b.name);
  })?.map((users) => {
    return (
      <Option key={users._id} value={users._id}>
        {users.name} - {users.user_name} - {users.email}
      </Option>
    );
  });
  const getAppRolesOptions = (app_id) => {
    let getAppList = ApplicationData.find((item) => item._id === app_id);
    let processedOptionList = ApplicationRolesList.filter(
      (text) => text.app_id === getAppList._id,
    );

    return processedOptionList.map((item) => {
      return {
        _id: item._id,
        role_name: item.role_name,
      };
    });
  };

  useEffect(() => {
    // relevant applications
    if (ApplicationData && ApplicationRolesList && PermissionData) {
      const relevantApps = ApplicationData.filter((app) =>
        APP_NAMES.includes(app.app_name),
      );

      const apps = relevantApps.map((app) => {
        const relevantAppRoles = ApplicationRolesList.filter(
          (appRole) => appRole.app_id === app._id,
        );
        const allocatedAppRole = relevantAppRoles.find((AppRole) =>
          PermissionData?.includes(AppRole._id),
        );
        return {
          appName: app.app_name,
          application_role_id: allocatedAppRole?._id ?? null,
          app_id: app._id,
        };
      });
      replace(apps);
    }
  }, [PermissionData, ApplicationData, ApplicationRolesList]);

  const resetFunction = () => {
    setUserID(undefined);
  };

  // Submit Functions
  // Add User Application Permissions react query call Options
  const QueryOptions = {
    onSuccess: () => {
      message.success("Permission Updated Successfully");
      resetFunction();
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  };

  // Add User Application Permissions
  const { mutate: mutateAddUserApplicationPermissions } =
    useAddUserApplicationPermissions(QueryOptions);

  const onSubmit = (data) => {
    const submitValues = data.config
      .map((element) => element.application_role_id)
      .filter((value) => value !== null);
    mutateAddUserApplicationPermissions({
      id: userID,
      data: { app_roles: submitValues },
    });
  };

  // reset function
  const onReset = () => {
    resetFunction();
  };

  return (
    <div>
      <div className="container_aa">
        <h3>SSO Integration</h3>
        <Breadcrumb
          routes={[
            { path: "/", breadcrumbName: "Dashboard" },
            {
              path: "sso-integration",
              breadcrumbName: "SSO Integration",
            },
          ]}
        />
      </div>
      <div className="container_ab">
        <div className="container_style_1_b">
          <form onSubmit={handleSubmit(onSubmit)}>
            <Collapse defaultActiveKey={["1"]} expandIconPosition="end">
              <Panel header="User Permission" key="1">
                <Form.Item {...itemStyles} label="User">
                  <Select
                    placeholder="Select a User "
                    showSearch
                    notFoundContent={null}
                    loading={isLoadingUserData}
                    optionFilterProp="children"
                    onChange={(values) => {
                      setUserID(values);
                    }}
                    value={userID}
                    filterOption={(input, option) => {
                      return (
                        option.children
                          .join(" ")
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      );
                    }}
                  >
                    {userList}
                  </Select>
                </Form.Item>
              </Panel>
            </Collapse>
            {SingleUserData && (
              <>
                <div className="is_form_2_top_bar">
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {SingleUserData?.role?.role_name ? (
                      <h3>{SingleUserData?.role?.role_name}</h3>
                    ) : null}
                  </div>
                </div>
                {fields.map((item, index) => {
                  return (
                    <SolutionAccessElement
                      control={control}
                      errors={errors}
                      key={item.id}
                      index={index}
                      app_name={item.appName}
                      options={getAppRolesOptions(item.app_id)}
                      isLoading={isLoadingPermissionData}
                    />
                  );
                })}
              </>
            )}

            <Form.Item wrapperCol={{ span: 12, offset: 10 }}>
              <Space>
                <Button onClick={onReset}>Cancel</Button>

                <Button
                  type="primary"
                  disabled={SingleUserData === undefined}
                  htmlType="submit"
                >
                  Update
                </Button>
              </Space>
            </Form.Item>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SSOIntegration;
